/*
 * Copyright (c) 2014-2023 Comparatio
 */

import { Component, OnDestroy, OnInit } from "@angular/core";
import { PasswordResetService } from "./password-reset.service";
import { Subscription } from "rxjs/Subscription";
import { AuthService } from "../auth.service";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { Title } from "@angular/platform-browser";
import { environment } from "../../../environments/environment";
import { versionInfo } from "../../version-info";
import { NgForm, FormGroup, FormBuilder, Validators } from "@angular/forms";
import { ScreenSizeService } from "../../services/screen-size.service";
import { CustomValidators } from "../../views/user/custom-validators";

@Component({
  selector: "app-password-reset",
  templateUrl: "./password-reset.component.html",
  styleUrls: ["./password-reset.component.scss"],
  providers: [PasswordResetService],
})
export class PasswordResetComponent implements OnInit, OnDestroy {
  public loading: boolean;
  public showReturnToLogin: boolean;
  public companyLogo: string;
  public companyName: string;
  public versionLabel: string;
  public errors: Array<any>;
  public message: String;
  public token: string;

  public isMobileScreen: boolean;
  private screenSizeService: ScreenSizeService;

  private subscriptions: Array<Subscription>;
  private passwordResetServiceRef = PasswordResetService;

  public frmSignup: FormGroup;

  constructor(
    private passwordResetService: PasswordResetService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastrService,
    private title: Title,
    private fb: FormBuilder
  ) {
    this.title.setTitle(this.companyName + " Portal - Password Reset");
    this.token = this.route.snapshot.queryParams.hasOwnProperty("token")
      ? this.route.snapshot.queryParams["token"]
      : null;
  }

  ngOnInit() {
    this.frmSignup = this.createSignupForm();
    this.screenSizeService = ScreenSizeService.getInstance();
    this.isMobileScreen = this.screenSizeService.isMobileScreen;
    if (environment.production) {
      this.versionLabel = versionInfo.number;
    } else {
      this.versionLabel = 'Test - ' + versionInfo.number;
    }
    if (this.authService.isAuthenticated) {
      this.router.navigate(['dashboard']);
    }

    this.loading = false;
    this.showReturnToLogin = false;
    this.subscriptions = [];
    this.errors = [];

    this.companyLogo = this.passwordResetServiceRef.DEFAULT_COMPANY_LOGO;
    this.companyName = this.passwordResetServiceRef.DEFAULT_COMPANY_NAME;
    this.message = "";

    this.subscriptions.push(
      this.authService.getLogo().subscribe((result) => {
        this.companyLogo = result.logo;
        this.companyName = result.name;
        this.title.setTitle(this.companyName + " Portal - Password Reset");
      })
    );
  }

  ngOnDestroy() {
    if (this.subscriptions.length > 0) {
      for (let index = 0; index < this.subscriptions.length; index++) {
        this.subscriptions[index].unsubscribe();
      }
    }
  }

  onSubmit() {
    if (this.frmSignup.valid) {
      if (this.token != null) {
        this.loading = true;
        let userData = this.frmSignup.value;
        userData.token = this.token;
        this.errors = [];
        this.passwordResetService.confirmPasswordReset(userData).subscribe(
          (result) => {
            this.showReturnToLogin = true;
            if (result.success) {
              this.message =
                "Your password was successfully updated. Please return to login and use your new credentials.";
            } else {
              this.errors = result.errors;
            }
            this.loading = false;
          },
          () => {
            this.loading = false;
            this.toast.error("Request timed out.", "Service Error");
          }
        );
      } else {
        this.showReturnToLogin = true;
        this.toast.warning(
          "This link appears broken. Please try again or request a new link.",
          "Unsuccessful",
          {
            timeOut: 5000,
            tapToDismiss: true,
          }
        );
      }
    } else {
      this.toast.error("Validations failed.", "error");
    }
  }

  createSignupForm(): FormGroup {
    return this.fb.group(
      {
        new_password: [
          null,
          Validators.compose([
            Validators.required,
            // check whether the entered password has a number
            CustomValidators.patternValidator(/\d/, {
              hasNumber: true,
            }),
            // check whether the entered password has upper case letter
            CustomValidators.patternValidator(/[A-Z]/, {
              hasCapitalCase: true,
            }),
            // check whether the entered password has a lower case letter
            CustomValidators.patternValidator(/[a-z]/, {
              hasSmallCase: true,
            }),
            // check whether the entered password has a special character
            CustomValidators.patternValidator(/[!#$.]/, {
              hasSpecialCharacters: true,
            }),
            Validators.minLength(8),
          ]),
        ],
        repeat_password: [null, Validators.compose([Validators.required])],
      },
      {
        // check whether our password and confirm password match
        validator: CustomValidators.passwordMatchValidator,
      }
    );
  }
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/auth/password-reset/password-reset.component.ts